import { Skeleton } from "@mui/joy";
import { Chip } from "@mui/joy";
import Color from "color";
import { ComponentProps } from "react";

import { useVenueCompetitionsByUuid } from "@/hooks/competitions";

import { useCurrentVenueUuid } from "../../hooks/venues";
import { getRepeatingColorsBg } from "../RouteCircle";

export const CircuitChip = ({
  label,
  colors,
  ...props
}: {
  label: string;
  colors: string[] | null;
} & ComponentProps<typeof Chip>) => {
  const allColors = colors?.flatMap((c) => c.split(":")) || [];
  const backgroundImage = getRepeatingColorsBg(allColors);
  let foregroundColor = "black";
  let useStroke = false;
  if (allColors.length == 1) {
    foregroundColor = Color(allColors[0]).isDark() ? "white" : "black";
  } else {
    foregroundColor = "white";
    useStroke = true;
  }

  return (
    <Chip
      {...props}
      sx={{
        color: foregroundColor,
        position: "relative",
        boxShadow: "0 2px 2px 0 #00000011, 0px 0px 0px 1.5px #00000033 inset",
        backgroundImage,
        fontWeight: "bold",
        borderRadius: 8,
        textShadow: useStroke
          ? "-1px -1px 1px #000, 1px -1px 1px #000, -1px 1px 1px #000, 1px 1px 1px #000"
          : null,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        textOverflow: "ellipsis",
        overflow: "hidden",
        paddingRight: 0.75,
        ...props.sx,
      }}
    >
      {label}
    </Chip>
  );
};

export const CircuitCell = ({ name, colors }: { name: string; colors: string[] }) => {
  return <CircuitChip label={name} colors={colors} />;
};

export const CompetitionCell = ({ name }: { name: string }) => {
  return <CircuitChip label={name} colors={["#0075FF"]} />;
};

export const CompetitionCategoriesCell = ({
  competitionUuid,
  categoryUuids,
}: {
  competitionUuid: string;
  categoryUuids: string[];
}) => {
  const venueUuid = useCurrentVenueUuid();
  const competitionsByUuid = useVenueCompetitionsByUuid(venueUuid);

  if (!competitionsByUuid) {
    return <Skeleton height={30} width={100} />;
  }
  const competition = competitionsByUuid?.[competitionUuid];
  const categories = competition?.stages[0].categories.filter((category) =>
    categoryUuids.includes(category.uuid),
  );
  return categories?.map((category) => (
    <CircuitChip key={category.uuid} label={category.name} colors={[category.color]} />
  ));
};
